import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  Site, SiteListResponse, SiteListParams, SiteInput,
} from '@/lib/types';

export async function fetchSites(params: SiteListParams): Promise<SiteListResponse> {
  const { data } = await apiClient.get<SiteListResponse>('/api/v1/sites', { params });
  return data;
}

export async function fetchSite(id: string): Promise<Site> {
  const { data } = await apiClient.get<{ site: Site }>(`/api/v1/sites/${id}`);
  return data.site;
}

export async function createSite(input: SiteInput): Promise<Site> {
  const { data } = await apiClient.post<{ site: Site }>('/api/v1/sites', input);
  return data.site;
}

export async function updateSite(id: string, input: Partial<SiteInput>): Promise<Site> {
  const { data } = await apiClient.patch<{ site: Site }>(`/api/v1/sites/${id}`, input);
  return data.site;
}

export function useSites(params: SiteListParams) {
  return useQuery({
    queryKey: ['sites', params],
    queryFn: () => fetchSites(params),
    placeholderData: (prev) => prev,
  });
}

export function useSite(id: string | null | undefined) {
  return useQuery({
    queryKey: ['site', id],
    queryFn: () => fetchSite(id!),
    enabled: !!id,
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSite,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<SiteInput> }) =>
      updateSite(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.setQueryData(['site', updated.id], updated);
    },
  });
}
